import { SimEventKind } from "@highmarch/domain";
import type { SimEvent } from "@highmarch/domain";

/**
 * The run's moments (ENGAGEMENT_2026-09-23 §B8, feel report #6/#7): what counts as a highlight, the budget that keeps
 * the callouts from turning into noise, the last-stand threshold and the clutch/comeback verdict at the end. Pure rules
 * over sim events and numbers — no DOM, no audio — so they are unit-tested; level/announcer.ts `LevelMoments` puts
 * them on screen and in the speakers.
 */
export const MOMENTS = {
  /** At most one callout per this many (real) seconds: a moment that fires every second is not a moment. */
  BudgetSec: 4,
  /** The fortress is in its last stand at or below this share of its HP (the HUD's red bar uses the same line). */
  LastStandFrac: 0.25,
  /** A win with this much fortress HP or less is a clutch victory. */
  ClutchHp: 3,
  /** A kill this close to the base (road cells still to walk) is a save: one more step and it would have leaked. */
  SavedCells: 1.5,
  /** Sim seconds between two SAVED! (and their hit-stops): a horde dying at the gate is one save, not twelve. */
  SavedGapSec: 6,
  /** Kills by one tower in one tick that make a MULTI-KILL callout (two is common splash; the hit-stop covers it). */
  MultiKillMin: 3,
  /** Stages cleared in a row without a leak that are called out. */
  StreakMarks: [5, 10, 15] as readonly number[],
  /** The tier that FIRST TIER N celebrates (the top tier of every tower today). */
  Tier: 3,
  /** Moments of the run kept for the share card. */
  TopKept: 3,
  /** Score per priority step when ranking the run's moments: the magnitude (kills, streak) orders within a kind. */
  ScorePerPriority: 100,
} as const;

export type MomentKind = "clutchVictory" | "comeback" | "bossDown" | "saved" | "multiKill" | "cleanStreak" | "firstTier";

/**
 * Per kind: priority when several callouts wait at once (higher wins; also ranks the run's top moments), and how long a
 * callout may wait for a free slot before it is stale — a SAVED! shown three seconds late no longer means anything,
 * a clean streak still does. The two end-of-level verdicts go to the victory screen, never through the budget.
 */
export const MOMENT_RULES: Record<MomentKind, { priority: number; waitSec: number }> = {
  clutchVictory: { priority: 7, waitSec: 0 },
  comeback: { priority: 6, waitSec: 0 },
  bossDown: { priority: 5, waitSec: 6 },
  saved: { priority: 4, waitSec: 1.5 },
  multiKill: { priority: 3, waitSec: 2 },
  cleanStreak: { priority: 2, waitSec: 8 },
  firstTier: { priority: 1, waitSec: 4 },
};

export interface Moment {
  readonly kind: MomentKind;
  readonly title: string;
  /** A second line (the boss's time, the tower's name); "" for none. */
  readonly sub: string;
  /** Rank among the run's moments (priority, then magnitude). */
  readonly score: number;
  /** Budget clock (s) when it happened; set by MomentBudget.offer. */
  at: number;
}

function moment(kind: MomentKind, title: string, sub = "", magnitude = 0): Moment {
  const m = Math.max(0, Math.min(MOMENTS.ScorePerPriority - 1, magnitude));
  return { kind, title, sub, score: MOMENT_RULES[kind].priority * MOMENTS.ScorePerPriority + m, at: 0 };
}

/** What the tracker needs to know about the sim beyond the events themselves (a LevelSim adapter in the level). */
export interface MomentWorld {
  readonly tick: number;
  readonly ticksPerSecond: number;
  /** Road cells the enemy still had to walk to the base when the event fired; null when it is unknown. */
  cellsToBase(enemyId: number): number | null;
  isBoss(enemyId: number): boolean;
  towerName(towerId: number): string;
}

/**
 * Reads each tick's events and says which moments just happened. Holds the run's counters: the clean streak, which
 * boss arrived when, whether a tower has reached the top tier yet, when the last save was.
 */
export class MomentTracker {
  private streak = 0;
  private leakedThisStage = false;
  private tierSeen = false;
  private lastSavedTick = -Infinity;
  private readonly bossSince = new Map<number, number>();

  /** Stages cleared in a row without a leak so far. */
  get cleanStreak(): number { return this.streak; }

  observe(events: readonly SimEvent[], w: MomentWorld): Moment[] {
    const out: Moment[] = [];
    let kills: Map<number, number> | null = null;
    for (const ev of events) {
      switch (ev.kind) {
        case SimEventKind.EnemyLeaked:
          this.leakedThisStage = true;
          this.streak = 0;
          break;
        case SimEventKind.StageCleared:
          if (!this.leakedThisStage) {
            this.streak++;
            if (MOMENTS.StreakMarks.includes(this.streak)) out.push(moment("cleanStreak", "CLEAN STREAK", `${this.streak} waves without a leak`, this.streak));
          }
          this.leakedThisStage = false;
          break;
        case SimEventKind.BossSpawned:
          this.bossSince.set(ev.a, w.tick);
          break;
        case SimEventKind.TowerUpgraded:
          if (!this.tierSeen && ev.b >= MOMENTS.Tier) {
            this.tierSeen = true;
            out.push(moment("firstTier", `FIRST TIER ${MOMENTS.Tier}`, w.towerName(ev.a)));
          }
          break;
        case SimEventKind.EnemyKilled: {
          if (ev.b > 0) {
            kills ??= new Map();
            kills.set(ev.b, (kills.get(ev.b) ?? 0) + 1);
          }
          if (w.isBoss(ev.a)) {
            const since = this.bossSince.get(ev.a);
            this.bossSince.delete(ev.a);
            const secs = since === undefined ? null : (w.tick - since) / w.ticksPerSecond;
            out.push(moment("bossDown", "BOSS DOWN", secs === null ? "" : `in ${secs.toFixed(1)} s`));
            break;
          }
          const left = w.cellsToBase(ev.a);
          if (left !== null && left <= MOMENTS.SavedCells && (w.tick - this.lastSavedTick) / w.ticksPerSecond >= MOMENTS.SavedGapSec) {
            this.lastSavedTick = w.tick;
            out.push(moment("saved", "SAVED!"));
          }
          break;
        }
        default:
          break;
      }
    }
    if (kills) {
      let best = 0;
      for (const n of kills.values()) best = Math.max(best, n);
      if (best >= MOMENTS.MultiKillMin) out.push(moment("multiKill", `MULTI-KILL ×${best}`, "", best));
    }
    return out;
  }
}

/**
 * The moment budget: callouts queue here and come out at most one per `BudgetSec`, never while `blocked` (a card offer
 * open, the game paused), the highest priority first when several wait; each drops once it has waited longer than its
 * kind allows. Every moment offered is also ranked for the run's top three, shown or not.
 */
export class MomentBudget {
  private clock = 0;
  private lastShown = -Infinity;
  private pending: Moment[] = [];
  private readonly kept: Moment[] = [];

  /** The run's best moments, best first (at most TopKept). */
  get top(): readonly Moment[] { return this.kept; }

  offer(m: Moment): void {
    m.at = this.clock;
    if (MOMENT_RULES[m.kind].waitSec > 0) this.pending.push(m);
    this.record(m);
  }

  /** Ranks a moment for the share card without calling it out (the end-of-level verdicts). */
  record(m: Moment): void {
    this.kept.push(m);
    // stable: an equal score keeps the earlier moment ahead
    this.kept.sort((a, b) => b.score - a.score);
    this.kept.length = Math.min(this.kept.length, MOMENTS.TopKept);
  }

  /** Advances the budget clock by `dt` real seconds; returns the callout to show now, if any. */
  update(dt: number, blocked: boolean): Moment | null {
    this.clock += dt;
    this.pending = this.pending.filter((m) => this.clock - m.at <= MOMENT_RULES[m.kind].waitSec);
    if (blocked || this.pending.length === 0 || this.clock - this.lastShown < MOMENTS.BudgetSec) return null;
    let pick = 0;
    for (let i = 1; i < this.pending.length; i++) {
      const a = this.pending[i], b = this.pending[pick];
      if (MOMENT_RULES[a.kind].priority > MOMENT_RULES[b.kind].priority || (a.kind === b.kind && a.score > b.score)) pick = i;
    }
    const m = this.pending.splice(pick, 1)[0];
    this.lastShown = this.clock;
    return m;
  }

  /** Drops what is waiting (the level ended). */
  clearPending(): void { this.pending = []; }
}

/** The fortress's low point over the run, for the end verdict. */
export class HpHistory {
  private low = 1;
  /** The lowest share of its HP the fortress has had this run. */
  get lowestFrac(): number { return this.low; }

  note(hp: number, max: number): void {
    if (max > 0) this.low = Math.min(this.low, Math.max(0, hp) / max);
  }

  /** CLUTCH VICTORY (won on ≤ ClutchHp), else COMEBACK (won after dropping into the last stand), else nothing. */
  verdict(won: boolean, hp: number, max: number): Moment | null {
    if (!won) return null;
    this.note(hp, max);
    if (hp <= MOMENTS.ClutchHp) return moment("clutchVictory", "CLUTCH VICTORY", `${hp} HP left`);
    if (this.low <= MOMENTS.LastStandFrac) return moment("comeback", "COMEBACK", `held from ${Math.round(this.low * max)} HP`);
    return null;
  }
}

/**
 * How deep into its last stand the fortress is: null above LastStandFrac (or dead, or no HP bar), else 0 at the line
 * rising to 1 at the last hit point's edge. Drives the heartbeat's tempo, the low-pass depth and the vignette.
 */
export function lastStandDepth(hp: number, max: number): number | null {
  if (!(max > 0) || hp <= 0) return null;
  const frac = hp / max;
  if (frac > MOMENTS.LastStandFrac) return null;
  return Math.max(0, Math.min(1, 1 - frac / MOMENTS.LastStandFrac));
}

/** Heartbeat period (s): a resting ~65 bpm on the line, racing to ~120 bpm at the last hit point. */
export const HEARTBEAT = { SlowSec: 0.92, FastSec: 0.5 } as const;
export function heartbeatInterval(depth: number): number {
  const k = Math.max(0, Math.min(1, depth));
  return HEARTBEAT.SlowSec + (HEARTBEAT.FastSec - HEARTBEAT.SlowSec) * k;
}
